/*!
 * TinyPoker 玩家查詢組件
 * 提供玩家搜尋、統計和歷史數據查詢功能
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputGroupText,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import axios from "axios";
import classnames from "classnames";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";
import TinyLoading, { TinyLoadingOverlay } from "components/tiny/TinyLoading";

import TinyPageHeader from "components/tiny/TinyPageHeader";
const API_BASE = "/api/tiny";

function TinyPlayerQuery() {
  // State
  const [weeks, setWeeks] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [players, setPlayers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [selectedWeek, setSelectedWeek] = useState("");
  const [keyword, setKeyword] = useState("");
  const [filterClub, setFilterClub] = useState("");
  const [filterType, setFilterType] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const LIMIT = 50;

  // Modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerHistory, setPlayerHistory] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  // 載入週列表
  const loadWeeks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/data/weeks`);
      if (res.data.code === 1) {
        setWeeks(res.data.data.weeks || []);
        if (res.data.data.weeks?.length > 0 && !selectedWeek) {
          setSelectedWeek(res.data.data.weeks[0]);
        }
      }
    } catch (err) {
      console.error("載入週列表失敗:", err);
    }
  }, [selectedWeek]);

  // 載入俱樂部列表
  const loadClubs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/clubs`);
      if (res.data.code === 1) {
        setClubs(res.data.data.clubs || []);
      }
    } catch (err) {
      console.error("載入俱樂部列表失敗:", err);
    }
  }, []);

  // 搜尋玩家
  const searchPlayers = useCallback(async (resetPage = true) => {
    if (!selectedWeek) return;
    
    setLoading(true);
    setError(null);
    
    const currentPage = resetPage ? 0 : page;
    if (resetPage) setPage(0);
    
    try {
      const params = new URLSearchParams();
      params.append("week", selectedWeek);
      params.append("limit", LIMIT);
      params.append("skip", currentPage * LIMIT);
      
      if (keyword) params.append("keyword", keyword);
      if (filterClub) params.append("club_id", filterClub);
      if (filterType) params.append("player_type", filterType);
      
      const res = await axios.get(`${API_BASE}/data/players?${params}`);
      if (res.data.code === 1) {
        setPlayers(res.data.data.players || []);
        setTotal(res.data.data.total || 0);
        setHasMore(res.data.data.page_info?.has_more || false);
      } else {
        setError(res.data.msg);
      }
    } catch (err) {
      setError("搜尋玩家失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  }, [selectedWeek, keyword, filterClub, filterType, page]);

  // 載入統計
  const loadStatistics = useCallback(async () => {
    if (!selectedWeek) return;
    
    try {
      const params = new URLSearchParams();
      params.append("week", selectedWeek);
      if (filterClub) params.append("club_id", filterClub);
      
      const res = await axios.get(`${API_BASE}/data/players/statistics?${params}`);
      if (res.data.code === 1) {
        setStatistics(res.data.data);
      }
    } catch (err) {
      console.error("載入統計失敗:", err);
    }
  }, [selectedWeek, filterClub]);

  // 初始載入
  useEffect(() => {
    loadWeeks();
    loadClubs();
  }, [loadWeeks, loadClubs]);

  // 當篩選條件改變時
  useEffect(() => {
    if (selectedWeek) {
      searchPlayers(true);
      loadStatistics();
    }
  }, [selectedWeek, filterClub, filterType]); // eslint-disable-line

  // 處理搜尋
  const handleSearch = () => {
    searchPlayers(true);
  };

  // 載入更多
  const handleLoadMore = () => {
    setPage(prev => prev + 1);
    searchPlayers(false);
  };

  // 打開玩家詳情
  const openPlayerDetail = async (player) => {
    setSelectedPlayer(player);
    setDetailModalOpen(true);
    setLoadingDetail(true);
    setActiveTab("1");
    
    try {
      // 載入玩家歷史
      const playerId = player.player?.id || player.player_id;
      const res = await axios.get(`${API_BASE}/data/players/${playerId}/history?limit=12`);
      if (res.data.code === 1) {
        setPlayerHistory(res.data.data.history || []);
      }
    } catch (err) {
      console.error("載入玩家歷史失敗:", err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // 格式化數字
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return parseFloat(num).toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  // 取得俱樂部名稱
  const getClubName = (clubId) => {
    const club = clubs.find(c => c.club_id === clubId);
    return club ? club.club_name : clubId;
  };

  // 提取玩家顯示資料
  const getPlayerDisplayData = (player) => {
    return {
      playerId: player.player?.id || player.player_id || "-",
      playerName: player.player?.nickname || player.player_name || "-",
      clubId: player.club?.id || player._meta?.club_id || "-",
      clubName: player.club?.name || player._meta?.club_name || "-",
      superAgentId: player.super_agent?.id || player.super_agent_id || "-",
      totalRake: player.total_rake || player.ring_games?.total_rake || 0,
      handsPlayed: player.ring_games?.hands_played || player.hands_played || 0,
      playerType: player.player_type || "unknown"
    };
  };

  return (
    <div className="content">
      {/* 這頁原本沒有 page header —— 標題埋在「主卡片」的 CardHeader 裡，
          而且在統計卡**下面**（先看到數字才知道這是什麼頁）。 */}
      <TinyPageHeader
        title="玩家查詢"
        description="依俱樂部、級別或 ID 查單一玩家的手數、Rake 與輸贏。"
      />

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 統計卡片 */}
      {statistics && (
        <Row className="mb-3">
          <Col md={3}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center icon-warning">
                      <i className="fa fa-users text-primary" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">總玩家數</p>
                      <CardTitle tag="p">{formatNumber(statistics.total_players)}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center icon-warning">
                      <i className="fa fa-user text-success" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">真人玩家</p>
                      <CardTitle tag="p">{formatNumber(statistics.real_players)}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center icon-warning">
                      <i className="fa fa-usd text-warning" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">總服務費</p>
                      <CardTitle tag="p">${formatNumber(statistics.total_rake)}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center icon-warning">
                      <i className="fa fa-bar-chart text-info" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">平均服務費</p>
                      <CardTitle tag="p">${formatNumber(statistics.avg_rake_per_player)}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* 主卡片 */}
      <Card>
        <CardBody>
          {/* 篩選區 */}
          <Row className="mb-3">
            <Col md={2}>
              <TinyWeekSelector
                label="週期"
                options={weeks}
                value={selectedWeek}
                onChange={setSelectedWeek}
                includeEmptyOption
              />
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>搜尋</Label>
                <InputGroup>
                  <Input
                    type="text"
                    placeholder="玩家 ID 或暱稱"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button color="primary" onClick={handleSearch}>
                    <i className="fa fa-search" />
                  </Button>
                </InputGroup>
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label>俱樂部</Label>
                <Input
                  type="select"
                  value={filterClub}
                  onChange={(e) => setFilterClub(e.target.value)}
                >
                  <option value="">全部</option>
                  {clubs.map((club) => (
                    <option key={club.club_id} value={club.club_id}>
                      {club.club_name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label>玩家類型</Label>
                <Input
                  type="select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">全部</option>
                  <option value="real">真人玩家</option>
                  <option value="agent">代理</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button 
                color="secondary" 
                size="sm" 
                onClick={() => { 
                  setKeyword(""); 
                  setFilterClub(""); 
                  setFilterType(""); 
                }}
                className="mr-2"
              >
                <i className="fa fa-refresh mr-1" />
                重置
              </Button>
              <small className="text-muted">
                共 {total} 筆結果
              </small>
            </Col>
          </Row>

          {/* 表格 */}
          {loading && players.length === 0 ? (
            <TinyLoading text="搜尋中" variant="block" />
          ) : players.length === 0 ? (
            <div className="text-center p-5">
              <i className="fa fa-inbox fa-3x text-muted mb-3" />
              <p className="text-muted">
                {selectedWeek ? "無符合條件的玩家" : "請選擇週期"}
              </p>
            </div>
          ) : (
            <TinyLoadingOverlay active={loading} text="搜尋中">
              <Table responsive hover>
                <thead className="text-primary">
                  <tr>
                    <th>玩家 ID</th>
                    <th>暱稱</th>
                    <th>俱樂部</th>
                    <th>超代</th>
                    <th>服務費</th>
                    <th>手數</th>
                    <th>類型</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((player, idx) => {
                    const data = getPlayerDisplayData(player);
                    return (
                      <tr key={data.playerId + idx}>
                        <td><code>{data.playerId}</code></td>
                        <td>{data.playerName}</td>
                        <td>
                          <small>{data.clubName}</small>
                        </td>
                        <td><small>{data.superAgentId}</small></td>
                        <td>${formatNumber(data.totalRake)}</td>
                        <td>{formatNumber(data.handsPlayed)}</td>
                        <td>
                          <Badge color={data.playerType === 'real' ? 'success' : 'secondary'}>
                            {data.playerType === 'real' ? '真人' : data.playerType === 'agent' ? '代理' : '未知'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            color="info"
                            size="sm"
                            onClick={() => openPlayerDetail(player)}
                          >
                            <i className="fa fa-eye" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              
              {hasMore && (
                <div className="text-center mt-3">
                  <Button color="primary" onClick={handleLoadMore}>
                    載入更多
                  </Button>
                </div>
              )}
            </TinyLoadingOverlay>
          )}
        </CardBody>
      </Card>

      {/* 玩家詳情 Modal */}
      <Modal isOpen={detailModalOpen} toggle={() => setDetailModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setDetailModalOpen(false)}>
          玩家詳情
        </ModalHeader>
        <ModalBody>
          {selectedPlayer && (
            <>
              {/* 基本資訊 */}
              <Row className="mb-3">
                <Col md={3}>
                  <strong>玩家 ID:</strong> {getPlayerDisplayData(selectedPlayer).playerId}
                </Col>
                <Col md={3}>
                  <strong>暱稱:</strong> {getPlayerDisplayData(selectedPlayer).playerName}
                </Col>
                <Col md={3}>
                  <strong>俱樂部:</strong> {getPlayerDisplayData(selectedPlayer).clubName}
                </Col>
                <Col md={3}>
                  <strong>超級代理:</strong> {getPlayerDisplayData(selectedPlayer).superAgentId}
                </Col>
              </Row>

              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "1" })}
                    onClick={() => setActiveTab("1")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fa fa-info-circle mr-1" />
                    當週詳情
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "2" })}
                    onClick={() => setActiveTab("2")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fa fa-history mr-1" />
                    歷史記錄 ({playerHistory.length})
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab} className="mt-3">
                <TabPane tabId="1">
                  <Row>
                    <Col md={6}>
                      <h6>Ring Games</h6>
                      <Table size="sm" borderless>
                        <tbody>
                          <tr>
                            <td>總服務費</td>
                            <td className="text-right">
                              ${formatNumber(selectedPlayer.ring_games?.total_rake || 0)}
                            </td>
                          </tr>
                          <tr>
                            <td>手數</td>
                            <td className="text-right">
                              {formatNumber(selectedPlayer.ring_games?.hands_played || 0)}
                            </td>
                          </tr>
                          <tr>
                            <td>保險費</td>
                            <td className="text-right">
                              ${formatNumber(selectedPlayer.insurance?.premium || 0)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6}>
                      <h6>MTT/SNG</h6>
                      <Table size="sm" borderless>
                        <tbody>
                          <tr>
                            <td>報名費</td>
                            <td className="text-right">
                              ${formatNumber(selectedPlayer.mtt?.total_fee || 0)}
                            </td>
                          </tr>
                          <tr>
                            <td>獎池貢獻</td>
                            <td className="text-right">
                              ${formatNumber(selectedPlayer.mtt?.prize_pool_contribution || 0)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="2">
                  {loadingDetail ? (
                    <TinyLoading text="載入中" variant="compact" style={{ justifyContent: 'center' }} />
                  ) : playerHistory.length === 0 ? (
                    <p className="text-muted text-center">無歷史記錄</p>
                  ) : (
                    <Table responsive size="sm">
                      <thead>
                        <tr>
                          <th>週期</th>
                          <th>俱樂部</th>
                          <th>服務費</th>
                          <th>手數</th>
                        </tr>
                      </thead>
                      <tbody>
                        {playerHistory.map((record, idx) => (
                          <tr key={record.week || idx}>
                            <td>{record.week || record._meta?.week}</td>
                            <td>{record.club?.name || record._meta?.club_name || "-"}</td>
                            <td>${formatNumber(record.ring_games?.total_rake || record.total_rake || 0)}</td>
                            <td>{formatNumber(record.ring_games?.hands_played || record.hands_played || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </TabPane>
              </TabContent>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDetailModalOpen(false)}>
            關閉
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default TinyPlayerQuery;


